// ============================================================================
// PersonaContext — shared persona classification store (Requirement 11)
//
// Single React Context that owns the persona classification state for the
// entire app. All chatbot and search surfaces consume persona data from this
// context instead of calling usePersona directly, ensuring consistent
// classification across all entry points without prop-drilling.
//
// Exports:
//   PersonaProvider   — wrap at app root (inside AuthContextProvider)
//   usePersonaContext — hook consumers call to access persona data
// ============================================================================

import React, { createContext, useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { usePersona } from 'src/Ateam-BI/AutoMate/hooks/usePersona';
import { AI_AGENT_ENABLED } from 'src/Ateam-BI/AutoMate/constants';

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const PersonaContext = createContext(null);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

/**
 * PersonaProvider wraps usePersona and exposes persona classification data
 * to all descendants via React context. Mount at the app root (or the
 * authenticated layout wrapper) so chatbot and search components can consume
 * persona data without prop-drilling.
 *
 * The provider reads the authenticated user's alias and firstName from the
 * Redux store (populated by TopNav after Cognito auth completes).
 */
export function PersonaProvider({ children }) {
  // Read user identity from Redux — same source as AgentChatProvider
  let alias = useSelector((globalState) => globalState.auth.userId);
  const firstName = useSelector((globalState) => globalState.auth.userFN);

  // Call usePersona once at the provider level — all consumers share this instance
  const {
    personaText,
    userName,
    personaClassification,
    effectivePersona,
    overrideMode,
    setOverrideMode,
    papiJobData,
  } = usePersona(alias, firstName || '', AI_AGENT_ENABLED);

  const contextValue = useMemo(
    () => ({
      personaText,
      userName,
      personaClassification,
      effectivePersona,
      overrideMode,
      setOverrideMode,
      papiJobData,
      alias,
    }),
    [
      personaText,
      userName,
      personaClassification,
      effectivePersona,
      overrideMode,
      setOverrideMode,
      papiJobData,
      alias,
    ]
  );

  return (
    <PersonaContext.Provider value={contextValue}>
      {children}
    </PersonaContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Consumer hook
// ---------------------------------------------------------------------------

/**
 * Access the shared persona classification store. Must be used within a
 * PersonaProvider.
 *
 * @returns {{
 *   personaText: string,
 *   userName: string,
 *   personaClassification: 'TECH' | 'NON_TECH' | 'UNKNOWN',
 *   effectivePersona: 'TECH' | 'NON_TECH',
 *   overrideMode: 'Auto' | 'TECH' | 'NON_TECH',
 *   setOverrideMode: (mode: 'Auto' | 'TECH' | 'NON_TECH') => void,
 *   papiJobData: { jobFamilyName: string, jobProfileName: string, jobLevel: string } | null,
 *   alias: string,
 * }}
 */
export function usePersonaContext() {
  const context = useContext(PersonaContext);
  if (context === null) {
    throw new Error(
      'usePersonaContext must be used within a <PersonaProvider>. ' +
      'Wrap your component tree with <PersonaProvider> at the app root.'
    );
  }
  return context;
}

export default PersonaProvider;
